import type { OgsmRemovalBlocker } from '@/types/api'

const fy = (fiscalYear: number) => `FY${String(fiscalYear).slice(-2)}`
const objectives = (count: number) => (count === 1 ? 'objective' : 'objectives')

export const OGSM_COPY = {
  title: 'OGSM',
  sessionLabel: fy,
  startSession: (fiscalYear: number) => `Start ${fy(fiscalYear)}`,
  currentSuffix: ' (current)',
  readOnly: 'Closed session, read only',
  portalReadOnly: 'Read only. Ask a company admin for edit access.',
  internalReadOnly: 'Read only. Ask a Greybull admin for edit access.',
  portalPublished: 'Published. Greybull reopens it for changes.',
  noSessions: 'This company has no OGSM yet.',
  loading: 'Loading',
  noGoal: 'No goal',
  noMeasure: 'No measure',
  unassigned: 'Unassigned',
  was: (original: string) => `was ${original}`,
  goalCaption: 'Goal',
  actualCaption: 'Actual',
  asOf: (date: string) => `As of ${date}`,
  strategiesOnTrack: (onTrack: number, total: number) =>
    `Strategies: ${onTrack} of ${total} on track`,
  showInactive: 'Show inactive',
  hideInactive: 'Hide inactive',
  inactiveHint: 'Achieved, Not Started and On Hold strategies',
  inactiveScope: 'Applies to every objective',
  showHidden: (n: number) => `Show ${n} hidden`,
  hideShown: (n: number) => `Hide ${n} inactive`,
  showHiddenLabel: (n: number) =>
    `Show ${n} hidden ${n === 1 ? 'strategy' : 'strategies'}. Shows inactive strategies on every objective.`,
  hideShownLabel: (n: number) =>
    `Hide ${n} inactive ${n === 1 ? 'strategy' : 'strategies'}. Hides inactive strategies on every objective.`,
  drillDown: 'Open this strategy one level down',
  upOneLevel: 'Up one level',
  parentObjective: 'Parent objective',
  drilledEmpty: (kind: string) =>
    `Nothing under this ${kind.toLowerCase()} yet. Use New strategy to add one.`,
  drilledEmptyReadOnly: (kind: string) =>
    `Nothing under this ${kind.toLowerCase()} yet.`,
  flaggedForScorecard: 'Flagged for scorecard',
  inScorecard: 'In scorecard',
  untitled: 'Untitled',
  statementLabel: 'Statement',
  addStatement: 'Add a statement',
  editStatement: 'Edit statement',
  showMore: 'Show more',
  showLess: 'Show less',
  changeStatus: 'Change status',
  assignTo: (kind: string) => `Assign ${kind.toLowerCase()} to`,
  changeOwner: 'Change owner',
  status: 'Status',
  greybull: 'Greybull',
  company: 'Company',
  includeInScorecard: 'Include in the Scorecard',
  linkMetric: 'Link to a metric',
  unlinkMetric: 'Unlink the metric',
  edit: 'Edit',
  delete: 'Delete',
  keepIt: 'Keep it',
  close: 'Close',
  nodeNotFound: 'OGSM Node not found',
  deleteTitle: (kind: string) => `Delete this ${kind.toLowerCase()}?`,
  deleteBody: (title: string) =>
    `"${title}" and everything beneath it will be removed.`,
  confirmDelete: 'Confirm delete',
  deleting: 'Deleting...',
  checkingForData: 'Checking for recorded data...',
  checkForDataFailed: (kind: string) =>
    `Couldn't check this ${kind.toLowerCase()} for recorded data.`,
  deleteHasDataBody: (title: string, descendantCount: number) =>
    `"${title}" has data recorded against it. Deleting it takes it${
      descendantCount === 0
        ? ''
        : descendantCount === 1
          ? ' and the 1 strategy beneath it'
          : ` and the ${descendantCount} strategies beneath it`
    } off this plan.`,
  recordedSoFar: 'Recorded so far',
  recordedLine: {
    readings: (n: number) =>
      n === 1 ? '1 actual entered' : `${n} actuals entered`,
    metricActuals: (n: number) =>
      n === 1
        ? '1 actual on a linked metric'
        : `${n} actuals on linked metrics`,
    targetRevisions: (n: number) =>
      n === 1
        ? '1 revision to a goal or measure'
        : `${n} revisions to goals or measures`,
    scorecardKpis: (n: number) =>
      n === 1
        ? '1 Scorecard entry from an earlier publish'
        : `${n} Scorecard entries from earlier publishes`,
  },
  deleteKeptNote:
    "Its history is kept. It just won't show on this plan anymore.",
  metricActualsNote: (shared: boolean) =>
    shared
      ? 'Actuals on a linked metric belong to the metric, so they still show wherever else that metric is used.'
      : 'Actuals on a linked metric belong to the metric and stay with it.',
  deleteBlocked: (code: OgsmRemovalBlocker, kind: string) =>
    ({
      OGSM_SESSION_PUBLISHED: `This session is published. Reopen it as a draft to delete this ${kind.toLowerCase()}.`,
      OGSM_SESSION_PREVIOUS_YEAR: 'Sessions from previous years are read-only.',
      OGSM_NODE_KPI_ACTIVE: `This ${kind.toLowerCase()} is still live on the Scorecard. Reopen the session as a draft first.`,
    })[code],
  goalType: 'Goal type',
  goalValue: 'Goal',
  addStrategy: 'Add strategy',
  cancel: 'Cancel',
  metricDialogHint: 'Pick the metric this goal is measured by.',
  actions: (kind: string) => `${kind} actions`,
  enterActual: 'Enter actual',
  updateActual: 'Update actual',
  actualValue: 'Actual value',
  actualDate: 'As of',
  newerActualShown: (date: string) =>
    `A newer reading from ${date} is still the one shown on the board.`,
  saveActual: 'Save actual',
  enterActuals: 'Enter actuals',
  enterActualsHint:
    'Every tracked metric on this board. Tab across, Enter to save. The as-of date is stored with each value.',
  enterActualsColumns: {
    metric: 'Metric',
    latestActual: 'Latest actual',
    asOf: 'As of',
  },
  enterActualsFooter:
    'Every change is stored as a new actual on the date shown.',
  enterActualsEmpty: 'No goals on this board take actuals yet.',
  latestActualFor: (title: string) => `Latest actual for ${title}`,
  asOfFor: (title: string) => `As of for ${title}`,
  actualSaved: 'Saved',
  done: 'Done',
  actualHistory: 'History',
  loadingHistory: 'Loading history',
  noActuals: 'No actuals yet',
  historyFor: (title: string) => `History for ${title}`,
  historyShowAll: (count: number) => `Show all ${count} actuals`,
  historyFailed: 'History could not be loaded.',
  tryAgain: 'Try again',
  pathUnder: 'Under:',
  fedBySource: 'Fed by its source, read only',
  reviseGoal: 'Revise goal',
  reviseMeasure: 'Revise measure',
  reviseHint: 'Choose how this change is recorded.',
  reviseEditHint:
    'Enter the new target. You choose how it is recorded when you save.',
  reviseChooseTitle: 'Revision or correction?',
  back: 'Back',
  reviseTypeHint:
    'Fix the unit if it was keyed wrong. Only a correction can change it.',
  reviseTypeNeedsCorrection: 'A unit change can only be a correction.',
  unit: 'Unit',
  unitWords: 'Unit words',
  newGoal: 'New goal',
  newMeasure: 'New measure',
  targetDate: 'Target date',
  correction: 'Correction',
  revision: 'Revision',
  correctionNote:
    'The number was keyed wrong. It is replaced and nothing is shown beneath it.',
  revisionNote:
    'The commitment changed. The original stays visible on the line beneath, permanently.',
  reason: 'Reason (optional)',
  save: 'Save',
  newObjective: 'New objective',
  addObjective: 'Add objective',
  createObjective: 'Create objective',
  newStrategy: 'New strategy',
  createStrategy: 'Create strategy',
  creating: 'Creating...',
  editObjective: 'Edit objective',
  editStrategy: 'Edit strategy',
  saveChanges: 'Save changes',
  saving: 'Saving...',
  under: (title: string) => `Under: ${title}`,
  titleLabel: 'Title',
  titlePlaceholder: 'e.g. Win the enterprise segment',
  saveOrCancelFirst: 'Save or cancel your changes first.',
  goalTypes: {
    METRIC: 'Chronograph metric',
    NUMBER: 'Number',
    CURRENCY: 'USD',
    PERCENT: 'Percent',
    DATE: 'Date',
    TEXT: 'Text',
  },
  goalTypeGroups: { numeric: 'Numeric', nonNumeric: 'Non-numeric' },
  goalTypeDescriptions: {
    METRIC:
      'Tied to a company metric from Chronograph. The title is written for you.',
    NUMBER: 'A custom goal in plain units.',
    CURRENCY: 'A custom goal in US dollars.',
    PERCENT: 'A custom goal as a percentage.',
    DATE: 'Achieved by a date. Progress follows the status.',
    TEXT: 'A written goal statement. Progress follows the status.',
  },
  metricField: 'Metric',
  goalStatement: 'Goal statement',
  customTitlePlaceholder: 'e.g. New franchise locations',
  goalTypeLocked: 'Fixed once created.',
  theObjective: 'The objective',
  theStrategy: 'The strategy',
  direction: 'Direction',
  verb: 'Verb',
  verbPlaceholder: 'Verb (optional)',
  verbSuggestions: [
    'Increase',
    'Decrease',
    'Maintain',
    'Achieve',
    'Open',
    'Complete',
    'Launch',
    'Sign',
    'Hire',
  ],
  objectiveText: 'Objective',
  strategyText: 'Strategy',
  objectivePlaceholder: 'e.g. the Midwest distribution centre',
  numericNote: (metric: string, unit: string) =>
    `${metric} — measured by a number. ${unit}, from the metric definition.`,
  qualitativeNote:
    'Qualitative — often simply done or not done. The verb is yours to write, or to leave out. The goal is the date it is achieved by.',
  textNote:
    'Qualitative — the goal is a short statement of what done looks like, due by the target date.',
  textGoalPlaceholder: 'Describe what done looks like',
  textCount: (n: number) => `${n} / 500`,
  achievedBy: 'Achieved by',
  includeOnPublish:
    'Include in the Compass scorecard when this session is published',
  sessionBarLabel: (fiscalYear: number) => `${fy(fiscalYear)} session`,
  draftState: 'Draft',
  publishedState: 'Published',
  draftNote: (flagged: number, total: number) =>
    `${flagged} of ${total} ${objectives(total)} flagged. Nothing reaches the Scorecard until this session is published.`,
  publishedNote: (date: string | null, count: number) =>
    `Published${date ? ` ${date}` : ''} · ${count} ${objectives(count)} on the Scorecard`,
  publishSession: 'Publish session',
  reopenAsDraft: 'Reopen as draft',
  reopenHint:
    'Take the session back to Draft and pull its objectives off the Scorecard',
  viewScorecard: 'View Scorecard',
  publishTitle: (fiscalYear: number) =>
    `Publish the ${fy(fiscalYear)} session?`,
  publishBody:
    'Publishing puts the flagged objectives on the Compass Scorecard. The plan is locked until you reopen it as a draft.',
  enteringScorecard: 'Entering the Scorecard',
  noneFlagged: 'No objectives are flagged for the Scorecard.',
  earlierYearNote:
    "Objectives published for an earlier year come off the Scorecard. They stay on that year's OGSM board.",
  keepAsDraft: 'Keep as draft',
  publishToScorecard: 'Publish to the Scorecard',
  publishing: 'Publishing...',
  publishRefused: "This session can't be published yet.",
  publishRefusedHelp:
    'The session stays in draft and nothing reached the Scorecard. Open the objective, link a metric again or unflag it, then publish again.',
  reopenTitle: (fiscalYear: number) => `Reopen the ${fy(fiscalYear)} session?`,
  reopenBody:
    'The session goes back to Draft and its objectives come off the Scorecard until it is published again. Nothing in the plan is deleted.',
  comingOffScorecard: 'Coming off the Scorecard',
  keepsFlagNote:
    'Each objective keeps its flag, so publishing again puts the same set back.',
  leavePublished: 'Leave it published',
  reopening: 'Reopening...',
  publishedSuccess: (fiscalYear: number) =>
    `${fy(fiscalYear)} is published. The flagged objectives are live on the Scorecard.`,
  reopenedSuccess: (fiscalYear: number) =>
    `${fy(fiscalYear)} is back in draft. Its objectives are off the Scorecard until it is published again.`,
  dismiss: 'Dismiss',
  directions: {
    INCREASE: 'Increase',
    DECREASE: 'Decrease',
    MAINTAIN: 'Maintain',
  },
  unitKinds: {
    CURRENCY: 'Currency',
    PERCENTAGE: 'Percentage',
    RATIO: 'Ratio',
    NUMBER: 'Plain count',
  },
  errors: {
    metricRequired: 'Metric is required',
    goalRequired: 'Goal is required',
    objectiveRequired: 'Objective is required',
    strategyRequired: 'Strategy is required',
    dateRequired: 'Target date is required',
    titleRequired: 'Title is required',
    textTooLong: 'Keep the goal to 500 characters',
  },
  reorder: {
    objectivesHint: 'Drag to reorder objectives',
    strategiesHint: 'Drag to reorder strategies',
    handleLabel: (title: string) => `Reorder “${title}”`,
    instructions:
      'Press Space or Enter to pick up. Use the up and down arrow keys to move, Space or Enter to drop, Escape to cancel.',
    pickedUp: (title: string) => `Picked up “${title}”.`,
    movedTo: (title: string, position: number, total: number) =>
      `“${title}” moved to position ${position} of ${total}.`,
    droppedAt: (title: string, position: number, total: number) =>
      `“${title}” dropped at position ${position} of ${total}.`,
    cancelled: (title: string, position: number) =>
      `Reorder cancelled. “${title}” is back at position ${position}.`,
  },
  kind: { objective: 'Objective', strategy: 'Strategy' },
  columns: {
    objectives: 'Objectives',
    goals: 'Goals',
    strategies: 'Strategies',
    measures: 'Measures',
  },
} as const
